import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MetricCard {
  id: string;
  label: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
  valueClassName?: string;
}

export interface ReportSummaryCardsProps {
  cards: MetricCard[];
  columns?: 2 | 3 | 4;
  className?: string;
}

export function ReportSummaryCards({
  cards,
  columns = 4,
  className,
}: ReportSummaryCardsProps) {
  const gridClassName = cn(
    "grid gap-4",
    columns === 2 && "grid-cols-1 sm:grid-cols-2",
    columns === 3 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    columns === 4 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    className
  );

  return (
    <div className={gridClassName}>
      {cards.map((card) => (
        <Card key={card.id} className={cn("overflow-hidden", card.className)}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.label}
            </CardTitle>
            {card.icon && (
              <card.icon className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <div
                className={cn(
                  "text-2xl font-bold",
                  card.valueClassName
                )}
              >
                {card.value}
              </div>
              {card.trend && (
                <div
                  className={cn(
                    "text-xs font-medium",
                    card.trend.isPositive
                      ? "text-finance-green"
                      : "text-finance-red"
                  )}
                >
                  {card.trend.isPositive ? "↑" : "↓"} {Math.abs(card.trend.value)}%
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
